import type { AIModelChoices, Annotation } from 'api';

import {
  getIgnoredLabels,
  getLabelGroups,
  LABEL_GROUPS,
  type LabelGroups,
  MODEL_FALLBACK_CATEGORIES,
} from '../../constants';

const DEFAULT_COLOR: readonly number[] = [128, 128, 128];

export interface GroupedAnnotation {
  groupName: string;
  colour: readonly number[];
  originalLabels: readonly string[];
}

export const getUniqueSegmentationCategories = (
  items: readonly Annotation[],
  model?: AIModelChoices
): Map<string, readonly number[]> => {
  const ignoredLabels = model ? getIgnoredLabels(model) : [];
  return items.reduce((categories, item) => {
    if (!categories.has(item.label) && !ignoredLabels.includes(item.label)) {
      categories.set(item.label, item.rgb_colour);
    }
    return categories;
  }, new Map<string, readonly number[]>());
};

export const getGroupedAnnotations = (
  annotations: readonly Annotation[],
  model: keyof typeof LABEL_GROUPS,
  showAerosols = false
): readonly GroupedAnnotation[] => {
  const uniqueCategories = getUniqueSegmentationCategories(annotations, model);
  const modelGroups = getLabelGroups({ showAerosols })[model];

  const { groups, groupedLabels } = createConfiguredGroups(modelGroups, uniqueCategories);

  const ungroupedLabels = findUngroupedLabels(uniqueCategories, groupedLabels);
  const fallbackGroups = createFallbackGroups(ungroupedLabels, model, uniqueCategories);

  return [...groups, ...fallbackGroups];
};

const createConfiguredGroups = (
  modelGroups: LabelGroups,
  uniqueCategories: Map<string, readonly number[]>
): {
  groups: readonly GroupedAnnotation[];
  groupedLabels: ReadonlySet<string>;
} => {
  const groupedLabels = new Set<string>();

  const groups = Object.entries(modelGroups)
    .filter(([_, groupConfig]) => groupConfig.categories.some((label) => uniqueCategories.has(label)))
    .map(([groupName, groupConfig]) => {
      const matchingLabels = groupConfig.categories.filter((label) => uniqueCategories.has(label));

      const firstLabel = matchingLabels[0];
      const groupColour = getGroupColour(groupConfig, uniqueCategories, firstLabel);

      for (const label of matchingLabels) {
        groupedLabels.add(label);
      }

      return {
        colour: groupColour,
        groupName,
        originalLabels: matchingLabels,
      };
    });

  return {
    groupedLabels: groupedLabels as ReadonlySet<string>,
    groups,
  };
};

const HEX_COLOUR_REGEX = /^#([0-9a-fA-F]{6})$/;

const parseHexColour = (value: string): readonly number[] | null => {
  const match = HEX_COLOUR_REGEX.exec(value);
  if (!match) {
    return null;
  }

  const hex = match[1];
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);

  return [red, green, blue];
};

const getGroupColour = (
  groupConfig: { colourOverride?: string },
  uniqueCategories: Map<string, readonly number[]>,
  firstLabel: string | undefined
): readonly number[] => {
  if (groupConfig.colourOverride) {
    const override = parseHexColour(groupConfig.colourOverride);
    if (override) {
      return override;
    }
  }

  if (firstLabel) {
    return uniqueCategories.get(firstLabel) || DEFAULT_COLOR;
  }

  return DEFAULT_COLOR;
};

const findUngroupedLabels = (
  uniqueCategories: Map<string, readonly number[]>,
  groupedLabels: ReadonlySet<string>
): readonly string[] => {
  return Array.from(uniqueCategories.keys()).filter((label) => !groupedLabels.has(label));
};

const createFallbackGroups = (
  ungroupedLabels: readonly string[],
  model: keyof typeof LABEL_GROUPS,
  uniqueCategories: Map<string, readonly number[]>
): readonly GroupedAnnotation[] => {
  if (ungroupedLabels.length === 0) {
    return [];
  }

  const fallbackCategory = MODEL_FALLBACK_CATEGORIES[model];

  if (fallbackCategory) {
    const firstLabel = ungroupedLabels[0];
    const colour = uniqueCategories.get(firstLabel) || DEFAULT_COLOR;

    return [
      {
        colour,
        groupName: fallbackCategory,
        originalLabels: ungroupedLabels,
      },
    ];
  }

  return ungroupedLabels.map((label) => {
    const colour = uniqueCategories.get(label) || DEFAULT_COLOR;
    return {
      colour,
      groupName: label,
      originalLabels: [label],
    };
  });
};

export const isGroupHidden = (group: GroupedAnnotation, hiddenCategories: readonly string[]): boolean => {
  return group.originalLabels.every((label) => hiddenCategories.includes(label));
};
